import React, { useState } from 'react';
import {
  Monitor, FileText, Receipt, FileCheck, ShoppingCart, Check, Plus, Trash2,
  Shield, Download, Headphones, Star, Clock, Users, Database, Wifi, Search,
  Cloud, X, ExternalLink, CheckCircle2, Zap, Award, CreditCard, Calendar,
  BarChart3, Wrench, Package, Play, Monitor as ScreenIcon, Layers, Truck,
  Calculator, ClipboardList,
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import CheckoutModal from './CheckoutModal';
import DownloadModal from './shoficina/DownloadModal';
import CartWidget from './shoficina/CartWidget';
import ModulosSection from './shoficina/ModulosSection';
import FaqSection from './shoficina/FaqSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── Data ──────────────────────────────────────────────────────────────
const modulos = [
  { id: 'agenda', icon: Calendar, title: 'Agenda', color: 'from-blue-500 to-blue-600',
    description: 'O módulo de agenda lhe auxilia a manter seus compromissos em dia, possibilitando agendar manutenções e revisões futuras, alertando quanto ao fim de prazos.',
    screens: [
      { title: 'Calendário de Agendamentos', img: './img/shoficina/agenda1.png' },
      { title: 'Cadastro de Compromissos',   img: './img/shoficina/agenda2.png' },
      { title: 'Alertas de Vencimento',      img: './img/shoficina/agenda3.png' },
    ] },
  { id: 'os', icon: ClipboardList, title: 'Ordens de Serviço', color: 'from-green-500 to-green-600',
    description: 'Nosso sistema possui um completo módulo de controle de ordens de serviço; capaz de gerenciar serviços prestados, serviços de terceiros, peças substituídas e muito mais.',
    screens: [
      { title: 'Lista de Ordens de Serviço', img: './img/shoficina/os1.png' },
      { title: 'Cadastro de OS',             img: './img/shoficina/os2.png' },
      { title: 'Impressão de OS',            img: './img/shoficina/os3.png' },
    ] },
  { id: 'vendas', icon: ShoppingCart, title: 'Vendas', color: 'from-orange-500 to-orange-600',
    description: 'O módulo de vendas lhe permite realizar vendas diretas a clientes no balcão de sua loja, emitir comprovantes de venda e enviá-los por e-mail, realizar orçamentos e muito mais.',
    screens: [
      { title: 'PDV - Ponto de Venda', img: './img/shoficina/vendas1.png' },
      { title: 'Orçamentos',           img: './img/shoficina/vendas2.png' },
      { title: 'Histórico de Vendas',  img: './img/shoficina/vendas3.png' },
    ] },
  { id: 'contas', icon: Calculator, title: 'Contas', color: 'from-purple-500 to-purple-600',
    description: 'Controle todas as despesas de seu negócio com o módulo de contas, cadastrando contas fixas, gerenciando suas contas a pagar e a receber, visualizando suas despesas e receitas no fluxo de caixa com relatórios financeiros.',
    screens: [
      { title: 'Contas a Pagar',    img: './img/shoficina/contas1.png' },
      { title: 'Contas a Receber',  img: './img/shoficina/contas2.png' },
      { title: 'Fluxo de Caixa',   img: './img/shoficina/contas3.png' },
    ] },
  { id: 'estoque', icon: Package, title: 'Estoque', color: 'from-cyan-500 to-cyan-600',
    description: 'O módulo de estoque gerencia as entradas e saídas de peças/produtos em seu estabelecimento; inclui controle de itens por número de série/lote, itens similares, múltiplas tabelas de preço e muito mais.',
    screens: [
      { title: 'Controle de Estoque',  img: './img/shoficina/estoque1.png' },
      { title: 'Cadastro de Produtos', img: './img/shoficina/estoque2.png' },
      { title: 'Movimentações',        img: './img/shoficina/estoque3.png' },
    ] },
  { id: 'controle', icon: Layers, title: 'Controle Completo', color: 'from-rose-500 to-rose-600',
    description: 'Tenha controle dos seus clientes, estoque, contas a pagar e a receber, contratos, equipamentos/veículos, fornecedores, compras e muito mais. Mantendo em um só sistema vários dados importantes ao bom funcionamento do seu negócio.',
    screens: [
      { title: 'Dashboard Geral',         img: './img/shoficina/controle1.png' },
      { title: 'Cadastro de Clientes',    img: './img/shoficina/controle2.png' },
      { title: 'Relatórios Gerenciais',   img: './img/shoficina/controle3.png' },
    ] },
];

const recursosAdicionais = [
  { text: 'Envio de mensagens automáticas por WhatsApp', icon: '💬' },
  { text: 'Backup automático (Local e Nuvem)',            icon: '☁️' },
  { text: 'Controle de acesso por usuário (Senhas)',      icon: '🔐' },
  { text: 'Agenda de compromissos integrada',             icon: '📅' },
  { text: 'Busca de endereço por CEP',                    icon: '📍' },
  { text: 'Relatórios gerenciais e gráficos',             icon: '📊' },
  { text: 'Impressão de etiquetas de código de barras',   icon: '🏷️' },
  { text: 'Integração com App Mobile',                    icon: '📱' },
  { text: 'Consulta de O.S. Online (OS Web)',             icon: '🌐' },
];

const products = [
  { id: 'shoficina', name: 'SHOficina', price: 268.90, badge: 'Mais vendido', icon: Monitor, color: 'from-blue-600 to-cyan-600',
    description: 'Sistema de gestão mais completo do mercado para oficinas mecânicas, autocenters e lojas de autopeças.',
    features: ['Ordens de Serviço','Controle de Estoque','Gestão Financeira','Cadastro de Clientes','Agenda Integrada','Relatórios Gerenciais'],
    addons: [
      { id: 'backup',   name: 'Backup Online Plus',       description: 'Proteja seus dados na nuvem',   price: 60.00,  icon: Cloud  },
      { id: 'placas20', name: '+20 Consultas de Placas/dia', description: 'Aumente sua produtividade', price: 335.00, icon: Search },
      { id: 'placas50', name: '+50 Consultas de Placas/dia', description: 'Alto volume de atendimentos', price: 685.00, icon: Search },
      { id: 'rede2',    name: 'Config. Rede 2 PCs',       description: 'Use em 2 computadores',        price: 90.00,  icon: Wifi   },
      { id: 'rede3',    name: 'Config. Rede 3 PCs',       description: 'Use em 3 computadores',        price: 135.00, icon: Wifi   },
    ] },
  { id: 'nfe',   name: 'SHNF-e - Emissor NF-e',        price: 409.90, badge: 'Exigido SEFAZ',         icon: FileText,  color: 'from-green-600 to-emerald-600',
    description: 'Emissão de Nota Fiscal Eletrônica modelo 55 (DANFE/A4). Obrigatória para revenda de peças e devoluções.',
    features: ['Nota Fiscal Eletrônica','Modelo 55 (DANFE)','Revenda de Peças','Devoluções'] },
  { id: 'nfce',  name: 'SHNFC-e - Cupom Fiscal',       price: 435.90, badge: 'Agilidade no Balcão',   icon: Receipt,   color: 'from-orange-600 to-amber-600',
    description: 'Nota Fiscal modelo 65 (Cupom Fiscal Eletrônico). Para venda rápida no balcão e PDV.',
    features: ['Cupom Fiscal Eletrônico','Modelo 65','Venda Rápida','Substitui Bloco Papel'] },
  { id: 'nfse',  name: 'SHNFS-e - Nota de Serviços',   price: 483.00, badge: 'Mais usado por oficinas', icon: FileCheck, color: 'from-purple-600 to-violet-600',
    description: 'Nota Fiscal de Serviços Eletrônica (NFS-e). Essencial para oficinas que cobram por serviços.',
    features: ['Nota Fiscal de Serviços','Emissão pela Prefeitura','Serviços Mecânicos','Obrigatório'] },
];

const faqs = [
  { question: 'O SHOficina funciona em rede?', answer: 'Sim! O SHOficina pode ser configurado para funcionar em rede local, permitindo que vários computadores acessem o mesmo banco de dados simultaneamente. Oferecemos pacotes de configuração para 2 ou 3 PCs.' },
  { question: 'Preciso de internet para usar?', answer: 'O sistema funciona offline após a validação inicial. A internet é necessária apenas para validação da licença, atualizações e recursos online como consulta de placas e backup na nuvem.' },
  { question: 'Como funciona a consulta de placas?', answer: 'O SHOficina oferece consulta automática de placas de veículos, trazendo informações como modelo, ano, cor e dados do proprietário. Você pode escolher pacotes de 20 ou 50 consultas diárias.' },
  { question: 'Posso emitir nota fiscal pelo sistema?', answer: 'Sim! Oferecemos módulos separados para NF-e (Nota Fiscal Eletrônica), NFC-e (Cupom Fiscal) e NFS-e (Nota de Serviços). Cada módulo é vendido separadamente para atender às necessidades específicas da sua oficina.' },
  { question: 'Qual a forma de pagamento?', answer: 'Aceitamos pagamento via Stripe (cartão de crédito e PIX). O processo é 100% seguro e você recebe a licença por e-mail após a confirmação do pagamento.' },
];

// ── Component ─────────────────────────────────────────────────────────
const SHOficinaERP = () => {
  const [cart, setCart] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [activeModuleScreens, setActiveModuleScreens] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const { toast } = useToast();

  const addToCart = (product) => {
    if (!cart.find(item => item.id === product.id)) {
      setCart([...cart, product]);
      toast({ title: 'Adicionado ao carrinho', description: `${product.name} foi adicionado com sucesso.` });
    }
  };
  const removeFromCart = (productId) => setCart(cart.filter(item => item.id !== productId));
  const isInCart = (productId) => cart.some(item => item.id === productId);
  const getTotal = () => cart.reduce((total, item) => total + item.price, 0);
  const clearCart = () => setCart([]);
  const handleCheckout = () => { if (cart.length > 0) setShowCheckout(true); };

  return (
    <section id="shoficina-erp"
      className="py-20 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900 relative overflow-hidden"
      style={{ scrollMarginTop: '120px' }}
      aria-label="SHOficina - Sistema de Gestão para Oficinas Mecânicas"
      itemScope itemType="https://schema.org/SoftwareApplication">

      {/* Schema.org */}
      <meta itemProp="name" content="SHOficina - Sistema de Gestão para Oficinas" />
      <meta itemProp="applicationCategory" content="BusinessApplication" />
      <meta itemProp="operatingSystem" content="Windows 10, Windows 11" />
      <meta itemProp="softwareVersion" content="6.0" />
      <link itemProp="url" href="https://fernandotech.com.br/#shoficina-erp" />
      <div itemProp="offers" itemScope itemType="https://schema.org/Offer">
        <meta itemProp="price" content="268.90" /><meta itemProp="priceCurrency" content="BRL" />
        <meta itemProp="availability" content="https://schema.org/InStock" />
      </div>
      <div itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
        <meta itemProp="ratingValue" content="4.9" /><meta itemProp="reviewCount" content="1250" /><meta itemProp="bestRating" content="5" />
      </div>

      {/* Background */}
      <div className="absolute inset-0 opacity-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <header className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-3 mb-6">
            <img src="./shoficina-logo.png" alt="SHOficina" className="h-16 w-auto" itemProp="image" loading="lazy" />
          </div>
          <span className="inline-block bg-cyan-500/20 text-cyan-400 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            Versão Desktop • Windows 10/11
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" itemProp="headline">
            Sistema de Gestão para <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Oficinas Mecânicas</span>
          </h2>
          <p className="text-xl text-blue-200 max-w-3xl mx-auto mb-6" itemProp="description">
            Conheça todas as ferramentas disponíveis no SHOficina - o software para oficina mecânica mais completo do Brasil.
            Controle ordens de serviço, estoque de peças, financeiro e emissão de notas fiscais.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {[
              { icon: Award,    color: 'text-yellow-400', text: '+170.000 Licenças Vendidas' },
              { icon: FileText, color: 'text-green-400',  text: '+11 Milhões de NFs Emitidas' },
              { icon: Clock,    color: 'text-cyan-400',   text: 'Desde 1980 no mercado' },
            ].map(({ icon: Icon, color, text }) => (
              <div key={text} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Icon className={`w-5 h-5 ${color}`} /><span className="text-white font-semibold">{text}</span>
              </div>
            ))}
          </div>
          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-6 items-end">
            <button onClick={() => setShowDownloadModal(true)}
              className="group flex flex-col items-center gap-3 bg-gradient-to-br from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 border-2 border-blue-500/30 hover:border-blue-400/50 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 shadow-xl hover:shadow-2xl hover:shadow-blue-500/20"
              data-testid="download-shoficina-btn" aria-label="Baixar SHOficina gratuitamente">
              <div className="w-20 h-20 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                <img src="./shoficina-logo.png" alt="SHOficina 6" className="w-12 h-12 object-contain" />
              </div>
              <div className="text-center"><span className="text-white font-bold text-lg block">SHOficina 6</span><span className="text-cyan-400 text-sm">Teste gratuitamente</span></div>
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 shadow-lg shadow-green-500/30 group-hover:from-green-500 group-hover:to-emerald-500 transition-all">
                <Download className="w-4 h-4" /> Baixar Grátis
              </span>
            </button>
            <button onClick={() => window.open('https://wa.me/5516991378312?text=Olá! Gostaria de saber mais sobre o SHOficina.', '_blank')}
              className="group flex flex-col items-center gap-3 bg-gradient-to-br from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 border-2 border-cyan-500/30 hover:border-cyan-400/50 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/20"
              aria-label="Falar com especialista via WhatsApp" data-testid="falar-especialista-btn">
              <div className="w-20 h-20 bg-gradient-to-b from-cyan-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform overflow-hidden">
                <img src="./specialist-avatar.png" alt="Especialista" className="w-16 h-16 object-cover object-top" />
              </div>
              <div className="text-center"><span className="text-white font-bold text-lg block">Especialista</span><span className="text-cyan-400 text-sm">Tire suas dúvidas</span></div>
              <span className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 shadow-lg shadow-cyan-500/30 group-hover:from-cyan-500 group-hover:to-blue-500 transition-all">
                <Headphones className="w-4 h-4" /> Falar com Especialista
              </span>
            </button>
          </div>
        </header>

        {/* Vídeo */}
        <article className="mb-16" aria-labelledby="video-title">
          <h2 id="video-title" className="text-2xl font-bold text-white text-center mb-8">
            <Play className="w-6 h-6 inline mr-2 text-red-500" /> Veja o SHOficina em Ação
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/20 border border-slate-700/50 bg-slate-800">
              <div className="aspect-video">
                <iframe src="https://www.youtube.com/embed/k64W5dixP_o?rel=0"
                  title="Demonstração do SHOficina"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen className="w-full h-full" loading="lazy" />
              </div>
            </div>
          </div>
        </article>

        {/* Módulos */}
        <ModulosSection modulos={modulos} activeModuleScreens={activeModuleScreens} setActiveModuleScreens={setActiveModuleScreens} />

        {/* Recursos Adicionais */}
        <article className="mb-16 bg-slate-800/40 rounded-2xl p-8 border border-slate-700/50" aria-labelledby="recursos-title">
          <h2 id="recursos-title" className="text-2xl font-bold text-white text-center mb-6">
            <Zap className="w-6 h-6 inline mr-2 text-yellow-400" /> Recursos Adicionais
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {recursosAdicionais.map((recurso, idx) => (
              <li key={idx} className="flex items-center gap-3 text-gray-300 bg-slate-900/30 p-3 rounded-lg">
                <span className="text-xl">{recurso.icon}</span><span className="text-sm">{recurso.text}</span>
              </li>
            ))}
          </ul>
          <footer className="mt-6 text-center">
            <p className="text-xs text-slate-500"><strong>Requisitos:</strong> Windows 10 ou 11 • 4GB de RAM (Recomendado) • Resolução mínima 1366x768 • Conexão com Internet para validação</p>
          </footer>
        </article>

        {/* Produtos */}
        <article className="mb-12" aria-labelledby="produtos-title">
          <h2 id="produtos-title" className="text-2xl font-bold text-white text-center mb-4">
            <ShoppingCart className="w-6 h-6 inline mr-2 text-cyan-400" /> Adquira sua Licença
          </h2>
          <div className="text-center mb-8">
            <a href="/comprar"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold px-8 py-4 rounded-xl text-lg shadow-xl shadow-green-500/30 transition-all hover:-translate-y-0.5"
              data-testid="comprar-online-btn">
              <CreditCard className="w-5 h-5" /> Comprar Online <ExternalLink className="w-4 h-4 ml-1" />
            </a>
            <p className="text-slate-400 text-sm mt-3">Compra segura via Stripe - Cartão e PIX</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map(product => {
              const Icon = product.icon;
              const inCart = isInCart(product.id);
              return (
                <article key={product.id}
                  className={`relative bg-slate-800/80 backdrop-blur-sm border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 ${inCart ? 'border-cyan-500 shadow-lg shadow-cyan-500/30' : 'border-slate-700/50 hover:border-cyan-500/50'}`}
                  itemScope itemType="https://schema.org/Product">
                  {product.badge && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">{product.badge}</span>
                    </div>
                  )}
                  <div className={`bg-gradient-to-r ${product.color} p-6`}>
                    <Icon className="w-12 h-12 text-white/90 mb-3" />
                    <h3 className="text-xl font-bold text-white" itemProp="name">{product.name}</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-400 text-sm mb-4 min-h-[60px]" itemProp="description">{product.description}</p>
                    <ul className="space-y-2 mb-6">
                      {product.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />{f}
                        </li>
                      ))}
                    </ul>
                    <div className="mb-4" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                      <meta itemProp="priceCurrency" content="BRL" />
                      <span className="text-gray-500 text-sm">A partir de</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-white" itemProp="price" content={product.price}>
                          R$ {product.price.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-gray-500 text-sm">/ano</span>
                      </div>
                      <link itemProp="availability" href="https://schema.org/InStock" />
                    </div>
                    <Button onClick={() => inCart ? removeFromCart(product.id) : addToCart(product)}
                      className={`w-full ${inCart ? 'bg-red-600 hover:bg-red-700' : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500'}`}
                      aria-label={inCart ? `Remover ${product.name}` : `Adicionar ${product.name}`}>
                      {inCart ? <><X className="w-4 h-4 mr-2" />Remover</> : <><Plus className="w-4 h-4 mr-2" />Adicionar</>}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </article>

        {/* FAQ */}
        <FaqSection faqs={faqs} expandedFaq={expandedFaq} setExpandedFaq={setExpandedFaq} />

        {/* Cart Widget */}
        {cart.length > 0 && (
          <CartWidget cart={cart} removeFromCart={removeFromCart} clearCart={clearCart} onCheckout={handleCheckout} />
        )}

        {/* Download Modal */}
        {showDownloadModal && (
          <DownloadModal
            onClose={() => setShowDownloadModal(false)}
            onDownload={() => toast({ title: 'Download iniciado!', description: 'O arquivo shoficina6.exe está sendo baixado.' })}
          />
        )}

        {/* Checkout Modal */}
        <CheckoutModal isOpen={showCheckout} onClose={() => setShowCheckout(false)}
          cart={cart} total={getTotal()}
          onSuccess={() => { setCart([]); setShowCheckout(false); }} />
      </div>
    </section>
  );
};

export default SHOficinaERP;
